import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import { Marker, APIProvider, Map, useMap } from "@vis.gl/react-google-maps";
import _, { debounce } from "lodash";

import SortBy from "./sort-by";
import MapHeader from "./map-header";
import LoadingSpinner from "./loading";
import AdvancedMarkers from "./marker";
import NoProperties from "./no-properties";
import PropertyDetails from "./property-details";
import FiltersDesktop from "../../../global/filter/FiltersDesktop";
import { filterItems } from "../../../global/filter/initialStates";
import { IoIosArrowForward, IoIosArrowBack } from "react-icons/io";
import PropertyDertailsMobileView from "./property-details-mobile-view";
import { availablePropertiesApi } from "../../../../api/propertyListingApi";

import "./styles.css";
import { useLocation } from "react-router-dom";
import toggleClass from "../../../../utils/dom/toggle-class.util";

const svgIcon = `
 <svg width="30" height="38" viewBox="0 0 30 38" fill="none" xmlns="http://www.w3.org/2000/svg" ><path opacity="0.24" fill-rule="evenodd" clip-rule="evenodd" d="M15 38C18.3137 38 21 37.1046 21 36C21 34.8954 18.3137 34 15 34C11.6863 34 9 34.8954 9 36C9 37.1046 11.6863 38 15 38Z" fill="black"></path><path d="M15 35.125L14.6846 35.513L15 35.7694L15.3154 35.513L15 35.125ZM15 35.125C15.3154 35.513 15.3155 35.5129 15.3157 35.5127L15.3163 35.5122L15.3185 35.5105L15.3266 35.5038L15.3577 35.4783C15.3849 35.4559 15.425 35.4229 15.477 35.3796C15.5811 35.293 15.7331 35.1654 15.9263 35.0005C16.3125 34.6707 16.8632 34.1912 17.5238 33.591C18.8441 32.3911 20.6063 30.7056 22.37 28.7648C24.1321 26.826 25.9059 24.6214 27.2418 22.3835C28.5721 20.155 29.5 17.8414 29.5 15.6964C29.5 7.39175 23.0268 0.625 15 0.625C6.97321 0.625 0.5 7.39175 0.5 15.6964C0.5 17.8414 1.42787 20.155 2.75818 22.3835C4.09413 24.6214 5.86793 26.826 7.62998 28.7648C9.39373 30.7056 11.1559 32.3911 12.4762 33.591C13.1368 34.1912 13.6875 34.6707 14.0737 35.0005C14.2669 35.1654 14.4189 35.293 14.523 35.3796C14.575 35.4229 14.6151 35.4559 14.6423 35.4783L14.6734 35.5038L14.6815 35.5105L14.6837 35.5122L14.6843 35.5127C14.6845 35.5129 14.6846 35.513 15 35.125Z" fill="#CC0000" stroke="white"></path><path d="M15.5 20.5C18.2614 20.5 20.5 18.2614 20.5 15.5C20.5 12.7386 18.2614 10.5 15.5 10.5C12.7386 10.5 10.5 12.7386 10.5 15.5C10.5 18.2614 12.7386 20.5 15.5 20.5Z" fill="white" stroke="white"></path></svg>
`;

const GoogleMapModal = ({ params, paramFilters, show = false, onHide, searchCriteria, setSearchCriteria, setErrors, closeGoogleMapModal, handleSort }) => {
  const [availableProperties, setAvailableProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState();
  const [boundingBox, setBoundingBox] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldRepan, setShouldRepan] = useState(false);
  const mapWrapperRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (!show || !boundingBox.minLat || !boundingBox.minLng || !boundingBox.maxLat || !boundingBox.maxLng || !boundingBox.zoom) {
      return;
    }

    setIsLoading(true);

    const payload = {
      ...params.propertyDetails,
      ...params.bookingDates,
      ...params.guests,
      ...params.filters,
      ...params.pagination,

      sortBy: params.sortOptions.by,
      sortOrder: params.sortOptions.order,

      searchRoomCountType: params.searchOptions.roomCountType,

      isMapSearch: true,
      ...boundingBox,
    };

    if (params.propertyDetails.propertyId) {
      payload.propertyLat = params.location.latitude;
      payload.propertyLng = params.location.longitude;
    }

    if (window.innerWidth < 992) {
      payload.markerPixelThreshold = 80;
    }

    availablePropertiesApi(payload)
      .then((res) => {
        setAvailableProperties(res.data.availableProperties);
      })
      .catch((error) => {
        toast.error(error.message || "An error occurred. Please try again later.");
        setAvailableProperties([]);
        setSelectedProperty(null);
      })
      .finally(() => {
        setIsLoading(false);
        setShouldRepan(false);
      });
  }, [boundingBox.minLat, boundingBox.minLng, boundingBox.maxLat, boundingBox.maxLng, boundingBox.zoom, show, location.search]);

  const debouncedSetBoundingBox = debounce((map) => {
    const bounds = map.getBounds();
    const zoom = map.getZoom();

    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();

    const maxLat = northEast.lat();
    const maxLng = northEast.lng();
    const minLat = southWest.lat();
    const minLng = southWest.lng();

    setBoundingBox({ maxLat, maxLng, minLat, minLng, zoom });
  }, 150);

  const onIdle = (e) => {
    debouncedSetBoundingBox(e.map);
  };

  return (
    <Modal
      className="property-listing-google-map-modal"
      size="xl"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      show={show}
      onHide={onHide}
      style={{ paddingRight: 0, padding: 0 }}
    >
      <Modal.Body>
        <div className="filters-list-google-map">
          <FiltersDesktop
            params={params}
            filters={[filterItems[2]]}
            paramFilters={paramFilters}
            isStarRatingNeeded={true}
            expandable={false}
            isPriceNeeded={false}
          />
        </div>
        <div
          className={`property-details-modal-main ${isExpanded ? "collapsed" : ""}`}
          style={{
            overflowY: isLoading ? "hidden" : "auto",
            ...(isLoading && { paddingRight: "25px" }),
          }}
        >
          {isLoading && <div className="loading-overlay"></div>}

          {availableProperties.length === 0 && !isLoading && (
            <div className="not-available-properties">
              <NoProperties setShouldRepan={setShouldRepan} />
            </div>
          )}
          {isLoading && <LoadingSpinner />}

          {availableProperties.length > 0 && (
            <div className={`property-details-shot-by ${isLoading && `property-details-shot-by-loading`}`}>
              <SortBy isLoading={isLoading} handleSort={handleSort} selectedSort={params.sortOptions.type} />

              <div className="property-detail">
                {availableProperties?.map((property) => {
                  return (
                    <PropertyDetails
                      isLoading={isLoading}
                      key={property.id}
                      property={property}
                      setSelectedProperty={setSelectedProperty}
                      selectedProperty={selectedProperty}
                      params={params}
                      likePropertyId={property.id}
                      setAvailableProperties={setAvailableProperties}
                      isActivateByHover={true}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <button className={`expand-button ${isExpanded ? "collapsed" : ""}`} onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded ? <IoIosArrowForward /> : <IoIosArrowBack />}
        </button>
        <div className={`google-map ${isExpanded ? "collapsed" : ""}`} ref={mapWrapperRef}>
          <APIProvider apiKey={process.env.REACT_APP_GOOGLE_API_KEY}>
            <MapHeader
              searchCriteria={searchCriteria}
              setSearchCriteria={setSearchCriteria}
              setErrors={setErrors}
              params={params}
              paramFilters={paramFilters}
              isExpanded={isExpanded}
              setIsExpanded={setIsExpanded}
              closeGoogleMapModal={closeGoogleMapModal}
            />

            <MapComponent
              availableProperties={availableProperties}
              mapWrapperRef={mapWrapperRef}
              onIdle={onIdle}
              location={params.location}
              setSelectedProperty={setSelectedProperty}
              selectedProperty={selectedProperty}
              params={params}
              shouldRepan={shouldRepan}
            />

            {selectedProperty?.id && (
              <PropertyDertailsMobileView setAvailableProperties={setAvailableProperties} property={selectedProperty} params={params} />
            )}
          </APIProvider>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default GoogleMapModal;

function extractTransformValues(transformString) {
  if (!transformString) {
    return { x: 0, y: 0 };
  }

  const transformValues = transformString.replace("transform:", "").trim();

  const translateFunctions = transformValues.split("translate(");

  const secondPart = translateFunctions[2] || "";
  const matches = secondPart.match(/(-?\d+\.?\d*)/g);

  const x = parseFloat(matches?.[0]) || 0;
  const y = parseFloat(matches?.[1]) || 0;

  return { x, y };
}

const MapComponent = ({ availableProperties, mapWrapperRef, location, onIdle, setSelectedProperty, selectedProperty, params, shouldRepan = false }) => {
  const map = useMap(null);

  const zoomlevel = calculateZoomLevel(location);

  useEffect(() => {
    if (!map || !location) return;

    map.panTo({ lat: location.latitude, lng: location.longitude });
    map.setZoom(zoomlevel);
  }, [map, location.country, location.province, location.district, location.city, location.label, location.latitude, location.longitude]);

  useEffect(() => {
    if (shouldRepan) {
      map.panTo({ lat: location.latitude, lng: location.longitude });
      map.setZoom(zoomlevel);
    }
  }, [shouldRepan]);

  const customSvgIcon = useMemo(() => {
    if (!map || !window.google?.maps?.Size) return null;

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgIcon)}`,
      scaledSize: new window.google.maps.Size(36.71, 44.49),
    };
  }, [map]);

  return (
    <Map
      ref={mapWrapperRef}
      scrollwheel={true}
      defaultCenter={{ lat: location.latitude, lng: location.longitude }}
      defaultZoom={zoomlevel}
      mapId={process.env.REACT_APP_GOOGLE_MAP_ID}
      fullscreenControl={false}
      streetViewControl={false}
      mapTypeControl={false}
      onIdle={onIdle}
      options={{
        maxZoom: 18,
        minZoom: 6,
        clickableIcons: false,
        visibility: "off",
      }}
      gestureHandling={"greedy"}
      onClick={() => {
        setSelectedProperty((prev) => {
          if (prev?.id) {
            const oldMarkerElement = document.querySelector(`.map-marker-mobile[data-property-id='${prev.id}']`);
            toggleClass(oldMarkerElement, "visited-marker");
            toggleClass(oldMarkerElement, "active");
          }

          return null;
        });
      }}
    >
      {_.isEmpty(params.propertyDetails.propertyId) && (
        <Marker icon={customSvgIcon} position={{ lat: location.latitude, lng: location.longitude }} zIndex={-1} />
      )}

      {availableProperties.map((property) => {
        const { latitude, longitude } = property;
        const position = { lat: latitude, lng: longitude };

        return (
          <AdvancedMarkers
            key={property.id}
            position={position}
            property={property}
            mapWrapperRef={mapWrapperRef}
            extractTransformValues={extractTransformValues}
            setSelectedProperty={setSelectedProperty}
            selectedProperty={selectedProperty}
            params={params}
          />
        );
      })}
    </Map>
  );
};

function calculateZoomLevel(location) {
  if (location.city) {
    return 13;
  } else if (location.district) {
    return 11.5;
  } else if (location.province) {
    return 10.8;
  } else if (location.country) {
    return 8;
  } else {
    return 10;
  }
}
